import React from "react";
import { Raffle } from "data/mockData";
import { Card } from "../card";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { getRaffleType } from "@hooks/functions/Raffle/type";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { Link } from "react-router-dom";
import { ProgressBar } from "react-bootstrap";
import { Masks } from "@hooks/functions/Masks/Masks";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { clickToCopy } from "@hooks/functions/Misc/ClickToCopy";
import { FaRegCopy } from "react-icons/fa";
import { formatName } from "@hooks/functions/formatName";
import dayjs from "dayjs";
import { config } from "config";

interface Props {
  raffle: any;
  index?: number;
  onClick?: (raffle: Raffle) => void;
}

const masks = new Masks();
export const RaffleCardSample: React.FC<Props> = ({
  raffle,
  index,
  onClick,
}) => {
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const raffleStatus = getRaffleStatus(raffle?.status as RaffleStatusTypes);
  const raffleType = getRaffleType(raffle?.type);
  const border = {
    default: "1px solid #e5e7eb",
    hover: "1px solid #e5e7eb",
  };

  return (
    <div
      className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4"
      style={{
        animation: `fadeInUp 0.5s ease-out ${(index || 1) * 0.08}s both`,
      }}
    >
      <Card
        className=""
        style={{
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          overflow: "hidden",
          border: border.default,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-4px)";
          e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,0.12)";
          e.currentTarget.style.borderColor = border.hover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
          e.currentTarget.style.borderColor = border.default;
        }}
      >
        {/* Card Header - Status Badges */}
        <div
          className="card-header border-0 pt-3 pb-2 px-3"
          style={{
            backgroundColor: "#ffffff",
            minHeight: "50px",
          }}
        >
          <div className="d-flex gap-2">
            <span
              className={`flex-fill badge bg-${raffleStatus?.color} text-center py-2`}
              style={{
                fontSize: "0.7rem",
                fontWeight: "600",
                letterSpacing: "0.3px",
                borderRadius: "6px",
              }}
              title={raffleStatus?.title}
            >
              {raffleStatus?.text}
            </span>
            <span
              className="flex-fill badge bg-dark text-center py-2"
              style={{
                fontSize: "0.7rem",
                fontWeight: "600",
                letterSpacing: "0.3px",
                borderRadius: "6px",
              }}
              title={raffleType?.title}
            >
              {raffleType?.text}
            </span>
          </div>
        </div>

        {/* Card Body */}
        <div className="card-body d-flex flex-column p-3 pt-0">
          {/* Title - Fixed Height with 4 lines support */}
          <Link
            to={`/campaign/default?campaign=${raffle?.campaign}${
              raffle?.key ? `&key=${raffle?.key}` : ""
            }`}
            className="text-decoration-none"
          >
            <div className="card-title py-3" title={raffle?.name}>
              <div className="row col-md-11 mx-auto">
                <span className="badge bg-primary fs-5">{raffle?.name}</span>
              </div>
            </div>
          </Link>

          {/* Image - Fixed Height */}
          <Link
            to={`/campaign/default?campaign=${raffle?.campaign}${
              raffle?.key ? `&key=${raffle?.key}` : ""
            }`}
          >
            <div
              className="raffle-image-container overflow-hidden mb-3"
              style={{
                height: "200px",
                backgroundColor: "#f3f4f6",
                position: "relative",
                borderRadius: "8px",
              }}
            >
              <img
                src={raffle?.thumb}
                alt={raffle?.name}
                className="w-100 h-100"
                style={{
                  objectFit: "cover",
                  transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              />
            </div>
          </Link>

          {/* Description - Fixed Height */}
          <p
            className="d-flex align-items-center justify-content-center text-muted text-center mb-3"
            style={{
              fontSize: "0.85rem",
              lineHeight: "1.4",
              height: "3.5rem",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={raffle?.description_short}
          >
            {raffle?.description_short}
          </p>

          {/* Progress Bar */}
          <div className="row mb-3">
            <div className="col-12 mx-auto text-center">
              <span className="fw-semibold">
                {raffle?.ticketSoldProgress}% dos tickets foram vendidos.
              </span>
              <ProgressBar
                animated
                now={raffle?.ticketSoldProgress}
                label={`${raffle?.ticketSoldProgress}%`}
                variant="primary"
              />
            </div>
          </div>

          {/* Stats Grid - Tickets Amount */}
          <div className="row">
            {/* Total Tickets Amount */}
            <div className="col-6 d-flex flex-column gap-2 mb-3">
              <div
                className="p-2 text-center"
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
                title="Quantidade total de Tickets"
              >
                <div className="d-flex flex-column">
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#9ca3af",
                      fontWeight: "600",
                      marginBottom: "2px",
                    }}
                  >
                    Total
                  </span>
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#1f2937",
                    }}
                  >
                    {masks.formatMilhar(Number(raffle?.quantity))}
                  </span>
                </div>
              </div>
            </div>

            {/* Total Sold Tickets Amount */}
            <div className="col-6 d-flex flex-column gap-2 mb-3">
              <div
                className="p-2 text-center"
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
                title="Quantidade total de Tickets Vendidos"
              >
                <div className="d-flex flex-column">
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#9ca3af",
                      fontWeight: "600",
                      marginBottom: "2px",
                    }}
                  >
                    Vendido(s)
                  </span>
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#1f2937",
                    }}
                  >
                    {masks.formatMilhar(Number(raffle?.ticketSoldAmount))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid - Ticket | Reward Prices */}
          <div className="row">
            {/* Total Unit Price  */}
            <div className="col-6 d-flex flex-column gap-2 mb-3">
              <div
                className="p-2 text-center"
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
                title="Preço unitário do Ticket."
              >
                <div className="d-flex flex-column">
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#9ca3af",
                      fontWeight: "600",
                      marginBottom: "2px",
                    }}
                  >
                    Preço Unitário
                  </span>
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#1f2937",
                    }}
                  >
                    {masks.brlCurrency(Number(raffle?.price))}
                  </span>
                </div>
              </div>
            </div>

            {/* Reward Market Price Total */}
            <div className="col-6 d-flex flex-column gap-2 mb-3">
              <div
                className="p-2 text-center"
                style={{
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
                title="Preço total do(s) prêmio(s)"
              >
                <div className="d-flex flex-column">
                  <span
                    style={{
                      fontSize: "0.7rem",
                      color: "#9ca3af",
                      fontWeight: "600",
                      marginBottom: "2px",
                    }}
                  >
                    Prêmio Total
                  </span>
                  <span
                    style={{
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      color: "#1f2937",
                    }}
                  >
                    {masks.brlCurrency(Number(raffle?.rewardPriceMarket))}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign & Owner Info */}
          <div className="d-flex flex-column gap-2 mb-2">
            {/* Capaign Slug */}
            <Link
              to={`/campaign/default?campaign=${raffle?.campaign}${
                raffle?.key ? `&key=${raffle?.key}` : ""
              }`}
            >
              <div
                className="col-12 badge bg-info text-white fs-6"
                style={{
                  color: "white",
                  borderRadius: "8px",
                  padding: "0.65rem 0.75rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onClick={() => {
                  clickToCopy({
                    text: `${window.location.origin}/campaign=${raffle?.campaign}`,
                    onSuccess: () =>
                      pushToastify({
                        message: "Copiado com sucesso!",
                        type: "success",
                        theme: "dark",
                      }),
                    onError: () =>
                      pushToastify({
                        message: "Falha ao copiar.",
                        type: "error",
                        theme: "dark",
                      }),
                  });
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#111827";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#1f2937";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                title="Clique para copiar o link da campanha"
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span
                    style={{
                      opacity: 0.85,
                    }}
                  >
                    Campanha:
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontFamily: "monospace",
                      letterSpacing: "0.5px",
                    }}
                  >
                    #{raffle?.campaign}
                  </span>
                </div>
              </div>
            </Link>

            {/* Campaign Code | URL */}
            <div
              className="col-12 badge text-black fs-6"
              style={{
                color: "white",
                borderRadius: "8px",
                padding: "0.65rem 0.75rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onClick={() => {
                clickToCopy({
                  text: raffle?.campaign,
                  onSuccess: () =>
                    pushToastify({
                      message: "Copiado com sucesso!",
                      type: "success",
                      theme: "dark",
                    }),
                  onError: () =>
                    pushToastify({
                      message: "Falha ao copiar.",
                      type: "error",
                      theme: "dark",
                    }),
                });
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
              title={`Clique para copiar a URL desta Campanha.`}
            >
              <div className="d-flex align-items-center justify-content-between">
                <span
                  style={{
                    opacity: 0.85,
                  }}
                >
                  <FaRegCopy />
                </span>
                <span
                  style={{
                    fontWeight: "700",
                    fontFamily: "monospace",
                    letterSpacing: "0.3px",
                  }}
                >
                  #{raffle?.campaign}
                </span>
              </div>
            </div>

            {/* Owner Info | Profile */}
            <Link
              to={`${config.urls.panel}/profile?atsign=${raffle.customerData.atSign}`}
              target={`_blank`}
            >
              <div
                className="col-12 badge bg-black text-white fs-6"
                style={{
                  color: "white",
                  borderRadius: "8px",
                  padding: "0.65rem 0.75rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#374151";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#4b5563";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                title={`Proprietário: ${formatName(raffle?.customerData?.name)}`}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <span
                    style={{
                      opacity: 0.85,
                    }}
                  >
                    Proprietário:
                  </span>
                  <span
                    style={{
                      fontWeight: "700",
                      fontFamily: "monospace",
                      letterSpacing: "0.3px",
                    }}
                  >
                    @{raffle?.customerData?.atSign}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Card Footer - Date Info */}
        <div
          className="card-footer border-0 px-3 pb-3 pt-0"
          style={{
            backgroundColor: "#ffffff",
            minHeight: "70px",
          }}
        >
          <div className="d-flex flex-column mt-2 gap-2">
            <div
              className="d-flex justify-content-between align-items-center py-1 px-2"
              style={{
                color: "#6b7280",
                backgroundColor: "#f9fafb",
                borderRadius: "6px",
              }}
            >
              <span style={{ fontWeight: "600" }}>Criada:</span>
              <span
                style={{
                  fontWeight: "700",
                  color: "#374151",
                  fontFamily: "monospace",
                }}
              >
                {dayjs(raffle?.createdAt).format("DD/MM/YY HH:mm")}
              </span>
            </div>

            {raffle?.finishedAt ? (
              <div
                className="d-flex justify-content-between align-items-center py-1 px-2"
                style={{
                  color: "#6b7280",
                  backgroundColor: "#f9fafb",
                  borderRadius: "6px",
                }}
              >
                <span style={{ fontWeight: "600" }}>Finalizada:</span>
                <span
                  style={{
                    fontWeight: "700",
                    color: "#374151",
                    fontFamily: "monospace",
                  }}
                >
                  {dayjs(raffle?.finishedAt).format("DD/MM/YY HH:mm")}
                </span>
              </div>
            ) : (
              <div className="text-center py-1">
                <span
                  className="badge bg-success"
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: "700",
                    padding: "0.4rem 1rem",
                    letterSpacing: "0.3px",
                  }}
                >
                  EM ANDAMENTO
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
